import { Elevation } from "--components/theme";
import Color from "color";
import styled, { DefaultTheme } from "styled-components";

type TabProps = {
    active?: boolean
    disabled?: boolean
    elevation?: Elevation
    radius?: keyof DefaultTheme["radius"]
    inactiveColor?: "secondary"|"contrast"
}

const Tab = styled.li<TabProps>`
    ${props => props.theme.typography.body1};

    position: relative;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    height: 54px;
    padding: ${props => props.theme.spacing(0, 4)};
    border-radius: ${props => `${props.theme.radius[props.radius || "radius"]} ${props.theme.radius[props.radius || "radius"]} 0 0`};

    margin: 0;
    background: ${props => props.active ? props.theme.palette.surface : 'transparent'};
    color: ${props => props.active ? props.theme.palette.text.primary : props.theme.palette.text[props.inactiveColor || "secondary"]};
    cursor: ${props => (props.disabled || props.active) ? 'initial' : 'pointer'};
    transition: background ${props => props.theme.transitions.toggle};

    &:hover {
        background: ${props => (props.disabled || props.active) ? '' : Color(props.theme.palette.surface).fade(0.75).string()}; 
    } 
    &:not(:first-child) {
        margin-left: ${props => props.theme.spacing(0.5)};
    }

    ${props => props.active && getElevationStyles(props.elevation || 0, props.radius || "large", props.theme)};
`;

const getElevationStyles = (elevation: Elevation, radius: keyof DefaultTheme["radius"], theme: DefaultTheme) => {
    switch (elevation) {
        case 1:
            return `
                box-shadow: ${theme.shadows.low}; 
                border: none; 
                &:after {
                    content: '';
                    position: absolute;
                    bottom: -32px;
                    left: -32px;
                    right: -32px;
                    height: 32px;
                    background: ${theme.palette.surface};
                }
                &:nth-child(1) {
                    &:after {
                        left:0;
                    }
                }
            `;
        case 2:
            return `
                box-shadow: ${theme.shadows.high}; 
                border: none; 
                &:after {
                    content: '';
                    position: absolute;
                    bottom: -32px;
                    left: -32px;
                    right: -32px;
                    height: 32px;
                    background: ${theme.palette.surface};
                }
                &:nth-child(1) {
                    &:after {
                        left:0;
                    }
                }
            `;
        default:
            return `
                &:before { /* Keeps box sizing simple adding border this way */
                    content: ''; 
                    position: absolute;
                    top: 0;
                    left: 0; 
                    right: 0;
                    bottom: 0;
                    border: 1px solid ${theme.palette.ui.border};
                    border-bottom: none;
                    border-radius: ${theme.radius.small} ${theme.radius.small} 0 0;
                    border-radius: ${theme.radius[radius || "large"]} ${theme.radius[radius || "large"]} 0 0;
                }
                &:after {
                    content: '';
                    position: absolute;
                    bottom: -1px;
                    left: 1px;
                    right: 1px;
                    height: 1px;
                    background: ${theme.palette.surface};
                }
            `;
    }
}

export default Tab;
export { Tab, TabProps };